import React, { FC, ReactNode } from "react";
import reactStringReplace from "react-string-replace";
import { CSSProperties } from "styled-components";
import {
  CustomText,
  OrderMessageFragment,
} from "../../types/graphql/generated";
import { StyledExternalLink, StyledTemplate } from "./index.styled";

interface ComponentMap {
  [key: string]: ReactNode;
}

interface TemplateAsComponentProps {
  className?: string;
  display?: CSSProperties["display"];
  map: ComponentMap;
  template: string;
}

/**
 * TemplateAsComponent is used to interpolate React/HTML nodes integrated into a
 * cohesive sentence (text formatting like bold or italic; link components).
 * Ex. usage: terms and conditions
 * For simple string interpolation use parseTemplateAsString
 */
export const TemplateAsComponent: FC<TemplateAsComponentProps> = ({
  className,
  display,
  map,
  template,
}) => {
  const parsedTemplate = reactStringReplace(
    template,
    /(\$\{.+?})/g,
    (match) => {
      const key = match.substring(2, match.length - 1).trim();
      return map[key];
    }
  );

  return (
    <StyledTemplate $display={display} className={className}>
      {parsedTemplate.map((val, i) => (
        <React.Fragment key={i}>{val}</React.Fragment>
      ))}
    </StyledTemplate>
  );
};

interface GetMapFromCustomText {
  customText: CustomText | OrderMessageFragment;
  disableLinking?: boolean;
  removeLinkColor?: boolean;
  underlineLinks?: boolean;
}

export const getMapFromCustomText = ({
  customText,
  disableLinking,
  removeLinkColor,
  underlineLinks,
}: GetMapFromCustomText) => {
  if (!customText.links) return {};
  return customText.links.reduce<Record<string, ReactNode>>((links, link) => {
    const { key, displayValue, linkValue } = link || {};

    if (!key) return links;

    let linkPrefix = "";
    if (link?.type === "phone") {
      linkPrefix = "tel:";
    } else if (link?.type === "email") {
      linkPrefix = "mailto:";
    }

    if (disableLinking) {
      links[key] = displayValue;
    } else {
      links[key] = (
        <StyledExternalLink
          href={`${linkPrefix}${linkValue}`}
          removeLinkColor={removeLinkColor}
          underline={underlineLinks}
        >
          {displayValue}
        </StyledExternalLink>
      );
    }

    return links;
  }, {});
};

interface TemplateAsComponentFromCustomText {
  customTexts?: (CustomText | OrderMessageFragment)[];
  disableLinking?: boolean;
  location: string;
  removeLinkColor?: boolean;
  type: string;
  underlineLinks?: boolean;
}

/**
 * Transforms a singular custom text to one or more TemplateAsComponents
 * (if multiple sentences in custom text body)
 *
 * In order for this to work correctly, make sure that the variables in the
 * body EXACTLY MATCH the key in an individual link. ex:
 *
 * - body: Please contact ${supportLinkText}
 * - key: supportLinkText
 */
export const TemplateAsComponentFromCustomText: FC<
  TemplateAsComponentFromCustomText
> = ({
  customTexts,
  disableLinking = false,
  location,
  removeLinkColor,
  type,
  underlineLinks = false,
}) => {
  const customText = customTexts?.find((text) => {
    return text.location === location && text.type === type;
  });

  if (!customText || !customText?.body) return null;

  const body =
    typeof customText.body === "string" ? [customText.body] : customText.body;

  const map = getMapFromCustomText({
    customText,
    disableLinking,
    removeLinkColor,
    underlineLinks,
  });

  return (
    <>
      {body.map((text) => (
        <TemplateAsComponent key={text} map={map} template={text} />
      ))}
    </>
  );
};
